import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Button,
  Alert,
  LinearProgress,
  Badge
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Chat as ChatIcon,
  TransferWithinAStation as TransferIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  EmojiEmotions as EmojiIcon,
  KeyboardArrowUp as TypingIcon,
  Psychology as PsychologyIcon,
} from '@mui/icons-material';
import websocketService from '../services/websocketService';
import firebaseService from '../services/firebaseService';
import aiTrainingService from '../services/aiTrainingService';
import ragService from '../services/ragService';
import './GemmaChatbot.css';

// Customer-focused training data
const customerTrainingData = {
  greetings: [
    "Hello! I'm your customer service assistant. How can I help you today?",
    "Hi there! Welcome to our service platform. What can I assist you with?",
    "Greetings! I'm here to help you find the perfect service. What are you looking for?"
  ],
  services: [
    "We offer a wide range of beauty and wellness services including Hair Styling, Facial, Manicure, Pedicure, Massage, Makeup, Waxing, Spa treatments, and more. What type of service are you interested in?",
    "Our platform connects you with qualified beauty and wellness service providers. You can browse by category or search for specific services like hair styling, skincare, nail care, and massage.",
    "From hair styling to spa treatments, we have beauty and wellness service providers for all your needs. What category interests you?"
  ],
  booking: [
    "To book a beauty or wellness service, browse our qualified service providers, select a service like hair styling or facial, choose your preferred time slot, and complete the booking process.",
    "Booking is easy! Find a beauty service provider, pick a service (hair, nails, massage, etc.), select your date and time, and confirm your booking.",
    "You can book beauty and wellness services through our platform by selecting a provider, choosing your service, and picking an available time slot."
  ],
  payment: [
    "We accept various payment methods including credit cards, debit cards, and digital wallets. All payments are processed securely.",
    "Payment is handled securely through our platform. You can pay using your preferred payment method when booking.",
    "We offer multiple secure payment options. You'll be charged when you confirm your booking."
  ],
  cancellation: [
    "You can cancel your booking up to 24 hours before the scheduled time. Cancellations are subject to our cancellation policy.",
    "To cancel a booking, go to your bookings section and select the booking you want to cancel. Cancellation policies vary by provider.",
    "Cancellations are allowed with advance notice. Check your booking details for specific cancellation terms."
  ],
  support: [
    "If you need help with your beauty service booking or have any questions, you can contact our customer support team through this chat or email us at support@nistylez.com",
    "For immediate assistance with your beauty and wellness services, use this chat or reach out to our support team. We're here to help!",
    "Customer support is available 24/7 for all your beauty service needs. You can also check our FAQ section for common questions about bookings, payments, and services."
  ],
  profile: [
    "You can update your profile information, manage your bookings, and view your service history in the Profile section.",
    "Your profile contains your personal information, booking history, and account settings. You can edit these anytime.",
    "Access your profile to view past bookings, update personal details, and manage your account preferences."
  ],
  ratings: [
    "After each service, you can rate and review your service provider. Your feedback helps other customers make informed decisions.",
    "We encourage you to rate your service providers after each booking. This helps maintain quality and assists other customers.",
    "You can leave ratings and reviews for service providers after your booking is completed. Your feedback is valuable to our community."
  ]
};

const GemmaChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth - 85, y: window.innerHeight - 85 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // AI Chat Session Management
  const [session, setSession] = useState(null);
  const [sessionStatus, setSessionStatus] = useState('ai'); // 'ai', 'waiting_for_agent', 'assigned_to_agent'
  const [queuePosition, setQueuePosition] = useState(null);
  const [estimatedWaitTime, setEstimatedWaitTime] = useState(null);
  const [showTransferButton, setShowTransferButton] = useState(false);
  
  // Dynamic training data
  const [dynamicTrainingData, setDynamicTrainingData] = useState(customerTrainingData);
  
  // Real-time chat features
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [assignedAgent, setAssignedAgent] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fabRef = useRef(null);
  const fileInputRef = useRef(null);

  // Quick response suggestions for customers
  const quickResponses = [
    "💇‍♀️ Find hair services near me",
    "🏪 Find providers near me",
    "🛍️ Scalp Products",
    "📅 Show my upcoming appointments",
    "❌ Cancel or reschedule booking",
    "❓ Help & support"
  ];

  // Load dynamic training data on component mount
  useEffect(() => {
    const loadTrainingData = async () => {
      try {
        const trainingData = await aiTrainingService.getCustomerTrainingData();
        setDynamicTrainingData(trainingData);
      } catch (error) {
        console.error('Error loading training data:', error);
        // Keep using default training data
      }
    };
    
    loadTrainingData();
  }, []);

  // Initialize WebSocket and Firebase connections
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Connect to WebSocket
      websocketService.connect(token);
      
      // Setup WebSocket listeners for chat
      websocketService.addEventListener('chatMessage', handleChatMessage);
      websocketService.addEventListener('typingIndicator', handleTypingIndicator);
      websocketService.addEventListener('agentAssigned', handleAgentAssigned);
      websocketService.addEventListener('agentDisconnected', handleAgentDisconnected);
      websocketService.addEventListener('chatSessionUpdate', handleChatSessionUpdate);
      websocketService.addEventListener('queueUpdate', handleQueueUpdate);
      websocketService.addEventListener('fileUploadProgress', handleFileUploadProgress);
      websocketService.addEventListener('messageRead', handleMessageRead);
      websocketService.addEventListener('connect', handleWebSocketConnect);
      websocketService.addEventListener('disconnect', handleWebSocketDisconnect);
    }

    return () => {
      // Cleanup WebSocket listeners
      websocketService.removeEventListener('chatMessage', handleChatMessage);
      websocketService.removeEventListener('typingIndicator', handleTypingIndicator);
      websocketService.removeEventListener('agentAssigned', handleAgentAssigned);
      websocketService.removeEventListener('agentDisconnected', handleAgentDisconnected);
      websocketService.removeEventListener('chatSessionUpdate', handleChatSessionUpdate);
      websocketService.removeEventListener('queueUpdate', handleQueueUpdate);
      websocketService.removeEventListener('fileUploadProgress', handleFileUploadProgress);
      websocketService.removeEventListener('messageRead', handleMessageRead);
      websocketService.removeEventListener('connect', handleWebSocketConnect);
      websocketService.removeEventListener('disconnect', handleWebSocketDisconnect);
    };
  }, []);

  // WebSocket event handlers
  const handleWebSocketConnect = () => {
    setIsConnected(true);
    console.log('✅ WebSocket connected for chat');
  };

  const handleWebSocketDisconnect = () => {
    setIsConnected(false);
    console.log('❌ WebSocket disconnected from chat');
  };

  const handleChatMessage = (data) => {
    if (data.sessionId === session?.sessionId) {
      addBotMessage(data.content, false, data.sender, data.messageType, data.metadata);
      
      // Mark message as read
      if (data.messageId) {
        websocketService.markMessageAsRead(data.messageId);
      }
    }
  };

  const handleTypingIndicator = (data) => {
    if (data.sessionId === session?.sessionId) {
      setIsAgentTyping(data.isTyping);
    }
  };

  const handleAgentAssigned = (data) => {
    if (data.sessionId === session?.sessionId) {
      setAssignedAgent(data.agent);
      setSessionStatus('assigned_to_agent');
      addBotMessage(`You are now connected with ${data.agent.name}. How can I help you today?`, true);
    }
  };

  const handleAgentDisconnected = (data) => {
    if (data.sessionId === session?.sessionId) {
      setAssignedAgent(null);
      setSessionStatus('ai');
      addBotMessage('The agent has disconnected. You are now back with the AI assistant.', true);
    }
  };

  const handleChatSessionUpdate = (data) => {
    if (data.sessionId === session?.sessionId) {
      setSessionStatus(data.status);
      if (data.queuePosition !== undefined) {
        setQueuePosition(data.queuePosition);
      }
      if (data.estimatedWaitTime !== undefined) {
        setEstimatedWaitTime(data.estimatedWaitTime);
      }
    }
  };

  const handleQueueUpdate = (data) => {
    if (data.sessionId === session?.sessionId) {
      setQueuePosition(data.queuePosition);
      setEstimatedWaitTime(data.estimatedWaitTime);
    }
  };

  const handleFileUploadProgress = (data) => {
    if (data.sessionId === session?.sessionId) {
      setFileUploadProgress(data.progress);
      if (data.progress === 100) {
        setTimeout(() => setFileUploadProgress(null), 1000);
      }
    }
  };

  const handleMessageRead = (data) => {
    if (data.sessionId === session?.sessionId) {
      setMessages(prev => prev.map(msg => 
        msg.id === data.messageId ? { ...msg, read: true } : msg
      ));
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Show welcome message when chat is first opened
      setTimeout(() => {
        addBotMessage("Hello! I'm your customer service assistant. How can I help you today? You can ask me about booking services, payments, cancellations, or anything else related to our platform.");
      }, 500);
    }
  }, [isOpen]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setPosition(prev => ({
        x: Math.min(prev.x, window.innerWidth - 85),
        y: Math.min(prev.y, window.innerHeight - 85)
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const addBotMessage = (text, isSystem = false, sender = 'bot', messageType = 'text', metadata = {}) => {
    setMessages(prev => [...prev, {
      id: Date.now() + Math.random(),
      text,
      sender,
      timestamp: new Date(),
      isSystem,
      messageType,
      metadata,
      read: false
    }]);
  };

  const addUserMessage = (text, messageType = 'text', metadata = {}) => {
    const messageId = Date.now() + Math.random();
    setMessages(prev => [...prev, {
      id: messageId,
      text,
      sender: 'user',
      timestamp: new Date(),
      messageType,
      metadata,
      read: false
    }]);
    return messageId;
  };

  // Start AI chat session with backend
  const startAIChatSession = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No auth token found, using local chat only');
        return;
      }

      const response = await fetch('/api/customer/ai-chat/sessions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          initialMessage: 'Hello, I need help',
          context: {
            intent: 'general',
            lastService: null,
            lastProvider: null,
            location: null
          }
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSession(data.session);
        setSessionStatus(data.session.status);
        
        // Join WebSocket chat room
        if (isConnected) {
          websocketService.joinChatRoom(data.session.sessionId);
        }
        
        // If there's an AI response, add it
        if (data.aiResponse) {
          addBotMessage(data.aiResponse.response);
        }
      }
    } catch (error) {
      console.error('Error starting AI chat session:', error);
      // Fallback to local chat
    }
  };

  // Send message to backend AI chat
  const sendAIMessage = async (message) => {
    if (!session) return null;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/customer/ai-chat/sessions/${session.sessionId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: message,
          messageType: 'text'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Update session status
        if (data.session) {
          setSessionStatus(data.session.status);
          setQueuePosition(data.session.queuePosition);
          setEstimatedWaitTime(data.session.estimatedWaitTime);
        }

        // Add AI response
        if (data.aiResponse) {
          addBotMessage(data.aiResponse.response);
          
          // Check if transfer was requested
          if (data.aiResponse.transferRequested) {
            setSessionStatus('waiting_for_agent');
            addBotMessage(`You are #${data.aiResponse.queuePosition} in the queue. Estimated wait time: ${data.aiResponse.estimatedWaitTime} minutes.`, true);
          }
        }

        return data.aiResponse;
      }
    } catch (error) {
      console.error('Error sending AI message:', error);
    }
    
    return null;
  };

  // Request human transfer
  const requestHumanTransfer = async () => {
    if (!session) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/customer/ai-chat/sessions/${session.sessionId}/transfer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reason: 'Customer requested human assistance'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSessionStatus('waiting_for_agent');
        setQueuePosition(data.session.queuePosition);
        setEstimatedWaitTime(data.session.estimatedWaitTime);
        addBotMessage('Your request has been transferred to a human agent. Please wait while we connect you.', true);
        addBotMessage(`You are #${data.session.queuePosition} in the queue. Estimated wait time: ${data.session.estimatedWaitTime} minutes.`, true);
        
        // Request human agent via WebSocket
        if (isConnected) {
          websocketService.requestHumanAgent('Customer requested human assistance');
        }
      }
    } catch (error) {
      console.error('Error requesting human transfer:', error);
      addBotMessage('Sorry, there was an error processing your transfer request.', true);
    }
  };

  const processMessage = async (message, messageType = 'text', metadata = {}) => {
    setIsTyping(true);
    
    // Handle support requests without human transfer
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes('human') || lowerMessage.includes('agent') || lowerMessage.includes('speak to someone') || lowerMessage.includes('transfer')) {
      addBotMessage("I'm here to help you with all your questions! I can assist with booking services, managing your account, and answering questions about our platform. How can I help you today?");
      setIsTyping(false);
      return;
    }

    // Send message via WebSocket if connected and session exists
    if (isConnected && session && sessionStatus === 'assigned_to_agent') {
      websocketService.sendChatMessage(message, messageType, metadata);
      setIsTyping(false);
      return;
    }

    // Try RAG system first for intelligent responses
    try {
      const ragResponse = await ragService.processQuery(message);
      if (ragResponse.isRAG) {
        addBotMessage(ragResponse.response);
        setIsTyping(false);
        return;
      }
    } catch (error) {
      console.log('RAG failed, falling back to traditional response:', error);
    }

    // Try backend AI if session exists
    if (session && sessionStatus === 'ai') {
      const aiResponse = await sendAIMessage(message);
      if (aiResponse) {
        setIsTyping(false);
        return;
      }
    }

    // Fallback to local keyword-based responses
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));

    let response = "";

    // Simple keyword-based response system for customer queries using dynamic training data
    if (lowerMessage.includes('hello') || lowerMessage.includes('hi') || lowerMessage.includes('hey')) {
      response = dynamicTrainingData.greetings[Math.floor(Math.random() * dynamicTrainingData.greetings.length)];
    } else if (lowerMessage.includes('service') || lowerMessage.includes('what') && lowerMessage.includes('offer')) {
      response = dynamicTrainingData.services[Math.floor(Math.random() * dynamicTrainingData.services.length)];
    } else if (lowerMessage.includes('book') || lowerMessage.includes('booking') || lowerMessage.includes('how') && lowerMessage.includes('book')) {
      response = dynamicTrainingData.booking[Math.floor(Math.random() * dynamicTrainingData.booking.length)];
    } else if (lowerMessage.includes('pay') || lowerMessage.includes('payment') || lowerMessage.includes('money')) {
      response = dynamicTrainingData.payment[Math.floor(Math.random() * dynamicTrainingData.payment.length)];
    } else if (lowerMessage.includes('cancel') || lowerMessage.includes('cancellation')) {
      response = dynamicTrainingData.cancellation[Math.floor(Math.random() * dynamicTrainingData.cancellation.length)];
    } else if (lowerMessage.includes('help') || lowerMessage.includes('support') || lowerMessage.includes('contact')) {
      response = dynamicTrainingData.support[Math.floor(Math.random() * dynamicTrainingData.support.length)];
    } else if (lowerMessage.includes('profile') || lowerMessage.includes('account')) {
      response = dynamicTrainingData.profile[Math.floor(Math.random() * dynamicTrainingData.profile.length)];
    } else if (lowerMessage.includes('rate') || lowerMessage.includes('review') || lowerMessage.includes('rating')) {
      response = dynamicTrainingData.ratings[Math.floor(Math.random() * dynamicTrainingData.ratings.length)];
    } else {
      response = "I'm here to help you with booking services, managing your account, and answering questions about our platform. Could you please be more specific about what you need help with? If you need more personalized assistance, I can connect you with a human agent.";
    }

    addBotMessage(response);
    setIsTyping(false);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const message = inputMessage.trim();
    addUserMessage(message);
    setInputMessage('');
    
    // Send typing indicator
    if (isConnected && session) {
      websocketService.sendTypingIndicator(true);
      setTimeout(() => websocketService.sendTypingIndicator(false), 1000);
    }
    
    await processMessage(message);
  };

  const handleQuickResponse = async (response) => {
    addUserMessage(response);
    setInputMessage('');
    
    await processMessage(response);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      handleFileUpload(file);
    }
  };

  const handleFileUpload = async (file) => {
    try {
      // Upload file to Firebase Storage
      const uploadResult = await firebaseService.uploadChatImage(file, session?.sessionId || 'temp');
      
      if (uploadResult.success) {
        const message = `📎 ${file.name}`;
        const metadata = {
          fileUrl: uploadResult.url,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type
        };
        
        addUserMessage(message, 'file', metadata);
        await processMessage(message, 'file', metadata);
      } else {
        addBotMessage('Sorry, there was an error uploading your file. Please try again.', true);
      }
    } catch (error) {
      console.error('File upload error:', error);
      addBotMessage('Sorry, there was an error uploading your file. Please try again.', true);
    }
    
    setSelectedFile(null);
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Start AI session when chat is opened
      setTimeout(() => {
        startAIChatSession();
        inputRef.current?.focus();
      }, 300);
    } else {
      // Leave chat room when closing
      if (session && isConnected) {
        websocketService.leaveChatRoom();
      }
    }
  };

  // Drag functionality
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = fabRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    
    setIsDragging(true);
    setDragOffset({ x: offsetX, y: offsetY });
  };

  const handleTouchStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    const rect = fabRef.current.getBoundingClientRect();
    const offsetX = touch.clientX - rect.left;
    const offsetY = touch.clientY - rect.top;
    
    setIsDragging(true);
    setDragOffset({ x: offsetX, y: offsetY });
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;

    // Keep within window bounds
    const maxX = window.innerWidth - 65;
    const maxY = window.innerHeight - 65;
    
    const clampedX = Math.max(0, Math.min(newX, maxX));
    const clampedY = Math.max(0, Math.min(newY, maxY));
    
    // Use requestAnimationFrame for smoother updates
    requestAnimationFrame(() => {
      setPosition({
        x: clampedX,
        y: clampedY
      });
    });
  }, [isDragging, dragOffset]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;

    const touch = e.touches[0];
    const newX = touch.clientX - dragOffset.x;
    const newY = touch.clientY - dragOffset.y;

    // Keep within window bounds
    const maxX = window.innerWidth - 65;
    const maxY = window.innerHeight - 65;
    
    const clampedX = Math.max(0, Math.min(newX, maxX));
    const clampedY = Math.max(0, Math.min(newY, maxY));
    
    // Use requestAnimationFrame for smoother updates
    requestAnimationFrame(() => {
      setPosition({
        x: clampedX,
        y: clampedY
      });
    });
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Function to render text with markdown links and formatting
  const renderTextWithLinks = (text) => {
    // First handle buttons: [BUTTON:query:label]
    const buttonRegex = /\[BUTTON:(.*?):(.*?)\]/g;
    let processedText = text;
    const buttons = [];
    let match;
    
    while ((match = buttonRegex.exec(text)) !== null) {
      const query = match[1];
      const label = match[2];
      buttons.push({ query, label, fullMatch: match[0] });
    }
    
    // Replace button markers with placeholders
    buttons.forEach((btn, index) => {
      processedText = processedText.replace(btn.fullMatch, `__BUTTON_${index}__`);
    });
    
    // Then handle strikethrough: ~~text~~
    processedText = processedText.replace(/~~(.*?)~~/g, '<del>$1</del>');
    
    // Then handle bold text: **text**
    processedText = processedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Then handle links: [text](url)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = processedText.split(linkRegex);
    const elements = [];
    
    for (let i = 0; i < parts.length; i += 3) {
      if (parts[i]) {
        // Check for button placeholders
        const textParts = parts[i].split(/(__BUTTON_\d+__)/);
        
        textParts.forEach((textPart, tIndex) => {
          const buttonMatch = textPart.match(/__BUTTON_(\d+)__/);
          if (buttonMatch) {
            const buttonIndex = parseInt(buttonMatch[1]);
            const button = buttons[buttonIndex];
            if (button) {
              elements.push(
                <Button
                  key={`button-${i}-${tIndex}`}
                  variant="contained"
                  size="small"
                  onClick={async () => {
                    // Add user message and process immediately
                    addUserMessage(button.query);
                    await processMessage(button.query);
                  }}
                  sx={{
                    my: 0.5,
                    mx: 0.5,
                    textTransform: 'none',
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0'
                    }
                  }}
                >
                  {button.label}
                </Button>
              );
            }
          } else if (textPart.trim()) {
            // Parse HTML and handle line breaks
            const htmlParts = textPart.split('\n');
            htmlParts.forEach((part, index) => {
              if (part.trim()) {
                elements.push(
                  <span 
                    key={`text-${i}-${tIndex}-${index}`}
                    dangerouslySetInnerHTML={{ __html: part }}
                  />
                );
              }
              if (index < htmlParts.length - 1) {
                elements.push(<br key={`br-${i}-${tIndex}-${index}`} />);
              }
            });
          }
        });
      }
      if (parts[i + 1] && parts[i + 2]) {
        // Check if this is a discount link (contains % OFF)
        const isDiscountLink = parts[i + 1].includes('% OFF');
        
        if (isDiscountLink) {
          // Render as red chip with white text
          elements.push(
            <Chip
              key={`chip-${i}`}
              label={parts[i + 1]}
              size="small"
              component="a"
              href={parts[i + 2]}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                backgroundColor: '#f44336',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '0.75rem',
                height: '20px',
                cursor: 'pointer',
                textDecoration: 'none',
                '&:hover': {
                  backgroundColor: '#d32f2f',
                  textDecoration: 'none',
                },
                '& .MuiChip-label': {
                  color: 'white !important',
                  fontWeight: 'bold',
                }
              }}
            />
          );
        } else {
          // Regular link
          elements.push(
            <a 
              key={`link-${i}`}
              href={parts[i + 2]} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ 
                color: '#1976d2', 
                textDecoration: 'underline',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {parts[i + 1]}
            </a>
          );
        }
      }
    }
    
    return elements;
  };

  const renderMessage = (message) => {
    const isUser = message.sender === 'user';
    const isSystem = message.isSystem;
    const isFile = message.messageType === 'file';

    return (
      <Box
        key={message.id}
        sx={{
          display: 'flex',
          justifyContent: isUser ? 'flex-end' : 'flex-start',
          mb: 2,
          opacity: isSystem ? 0.7 : 1
        }}
      >
        <Box
          sx={{
            maxWidth: '70%',
            backgroundColor: isUser ? '#1976d2' : (isSystem ? '#f5f5f5' : '#ffffff'),
            color: isUser ? '#ffffff' : '#000000',
            borderRadius: 2,
            padding: 2,
            boxShadow: 1,
            border: isSystem ? '1px solid #e0e0e0' : 'none'
          }}
        >
          {isFile ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                📎 {message.metadata?.fileName || 'File'}
              </Typography>
              {message.metadata?.fileUrl && (
                <img 
                  src={message.metadata.fileUrl} 
                  alt="Uploaded file"
                  style={{ maxWidth: '100%', borderRadius: 4 }}
                />
              )}
            </Box>
          ) : (
            <Typography variant="body2" component="div">
              {renderTextWithLinks(message.text)}
            </Typography>
          )}
          <Typography variant="caption" sx={{ opacity: 0.7, mt: 1, display: 'block' }}>
            {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <>
      {/* GEMMA AI Floating Button - Draggable */}
      <div
        ref={fabRef}
        className="gemma-floating-button"
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: '65px',
          height: '65px',
          zIndex: 1000,
          pointerEvents: 'auto',
          background: 'linear-gradient(145deg, #2196F3 0%, #1976D2 50%, #0D47A1 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: isDragging ? 'grabbing' : 'grab',
          boxShadow: isDragging 
            ? '0 12px 35px rgba(33, 150, 243, 0.5), 0 6px 16px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)' 
            : '0 8px 25px rgba(33, 150, 243, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
          border: '2px solid rgba(255, 255, 255, 0.9)',
          transition: isDragging ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isDragging ? 'scale(1.1)' : 'scale(1)',
          userSelect: 'none',
          touchAction: 'none',
          visibility: 'visible',
          opacity: '1',
          clip: 'auto',
          overflow: 'visible',
          minWidth: '65px',
          minHeight: '65px',
          maxWidth: '65px',
          maxHeight: '65px'
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onMouseEnter={(e) => {
          if (!isDragging) {
            e.currentTarget.style.transform = 'scale(1.08)';
            e.currentTarget.style.boxShadow = '0 12px 35px rgba(33, 150, 243, 0.5), 0 6px 16px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isDragging) {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(33, 150, 243, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
          }
        }}
        onClick={(e) => {
          if (!isDragging) {
            toggleChat();
          }
        }}
      >
        <div style={{
          color: 'white',
          display: 'grid',
          placeItems: 'center',
          width: '100%',
          height: '100%',
          padding: '0',
          margin: '0',
          gridTemplateRows: 'auto auto',
          gap: '4px',
          position: 'relative'
        }}>
          {isOpen ? (
            <CloseIcon style={{ 
              fontSize: '28px', 
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))',
              justifySelf: 'center'
            }} />
          ) : (
            <>
              <BotIcon style={{ 
                fontSize: '28px', 
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))',
                justifySelf: 'center'
              }} />
              <div style={{ 
                fontSize: '9px', 
                fontWeight: 'bold',
                letterSpacing: '0.8px',
                lineHeight: '1',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                textAlign: 'center',
                justifySelf: 'center'
              }}>GEMMA</div>
            </>
          )}
          
          {/* Drag indicator */}
          <div style={{
            position: 'absolute',
            bottom: '2px',
            right: '2px',
            width: '12px',
            height: '12px',
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div style={{
              width: '4px',
              height: '4px',
              background: 'rgba(255, 255, 255, 0.8)',
              borderRadius: '50%'
            }} />
          </div>
        </div>
      </div>

      {/* Chat Dialog */}
      <Dialog
        open={isOpen}
        onClose={toggleChat}
        maxWidth="sm"
        fullWidth
        className="gemma-chatbot"
        PaperProps={{
          sx: {
            position: 'fixed',
            bottom: 20,
            right: 20,
            margin: 0,
            maxHeight: '70vh',
            minHeight: '400px'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {assignedAgent ? (
              <>
                <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                  {assignedAgent.name?.charAt(0) || 'A'}
                </Avatar>
                <Typography variant="h6">
                  {assignedAgent.name} (Agent)
                </Typography>
              </>
            ) : (
              <>
                <BotIcon />
                <Typography variant="h6">
                  Gemma AI Assistant
                </Typography>
              </>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Session status */}
            {sessionStatus === 'waiting_for_agent' && (
              <Chip 
                label={`Queue: #${queuePosition}`} 
                size="small" 
                color="warning"
                variant="outlined"
              />
            )}
            
            <IconButton onClick={toggleChat} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Messages Area */}
          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto', 
            p: 2,
            backgroundColor: '#f5f5f5'
          }}>
            {messages.map(renderMessage)}
            
            {/* Typing indicator */}
            {isAgentTyping && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <CircularProgress size={16} />
                  <CircularProgress size={16} sx={{ animationDelay: '0.2s' }} />
                  <CircularProgress size={16} sx={{ animationDelay: '0.4s' }} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Agent is typing...
                </Typography>
              </Box>
            )}
            
            {/* File upload progress */}
            {fileUploadProgress !== null && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress variant="determinate" value={fileUploadProgress} />
                <Typography variant="caption" color="text.secondary">
                  Uploading file... {fileUploadProgress}%
                </Typography>
              </Box>
            )}
            
            {/* Quick Responses */}
            {messages.length === 1 && (
              <Box sx={{ mt: 1 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 2, 
                    display: 'block',
                    fontWeight: 600,
                    color: '#1976d2',
                    fontSize: '0.875rem'
                  }}
                >
                  💡 Quick Actions
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {quickResponses.map((response, index) => (
                    <Chip
                      key={index}
                      label={response}
                      size="medium"
                      onClick={() => handleQuickResponse(response)}
                      className="quick-response-chip"
                      sx={{ 
                        cursor: 'pointer',
                        backgroundColor: '#1976d2 !important',
                        color: 'white !important',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                        height: '36px',
                        borderRadius: '18px',
                        border: 'none',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                        transition: 'all 0.3s ease',
                        '& .MuiChip-label': {
                          color: 'white !important',
                          fontWeight: 500,
                        },
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 16px rgba(212, 175, 55, 0.4)',
                          backgroundColor: '#D4AF37 !important',
                          '& .MuiChip-label': {
                            color: 'black !important',
                          },
                        },
                        '&:active': {
                          transform: 'translateY(0px)',
                          boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)',
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton 
                size="small" 
                onClick={() => fileInputRef.current?.click()}
                disabled={!isConnected}
                sx={{ display: 'none' }}
              >
                <AttachFileIcon />
              </IconButton>
              
              <TextField
                ref={inputRef}
                fullWidth
                variant="outlined"
                placeholder="Type your message..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isTyping}
                size="small"
                className="input-field"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
              
              <IconButton
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isTyping}
                className="send-button"
                sx={{
                  width: 40,
                  height: 40
                }}
              >
                {isTyping ? <CircularProgress size={20} className="loading-spinner" /> : <SendIcon />}
              </IconButton>
            </Box>
            
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf,.doc,.docx"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GemmaChatbot;
